/**
 * canvasEviction — le canvas quitte les lieux quand l'avocat change (#856).
 *
 * ## Pourquoi ce module est né avec #856
 *
 * Le canvas réside désormais dans la coque, qui reste montée **à travers un
 * changement de compte** — c'est déjà le piège documenté pour la liste des
 * conversations (incident resource-sharing, 2026-07-07 : le compte B voyait
 * brièvement une ligne du compte A). Auparavant, changer de compte démontait
 * l'écran assistant et emportait le canvas avec lui ; ce nettoyage était donc
 * gratuit, et invisible. Il ne l'est plus.
 *
 * Sans cette éviction, l'avocat B ouvrirait le canvas et y trouverait le
 * document de l'avocat A — pas une ligne de liste, un acte entier.
 *
 * ## Le bail, et pourquoi effacer les stores ne suffit pas
 *
 * Une partie de ce qu'il faut évincer ne vit dans **aucun store** : l'état
 * accumulé du suivi de streaming (les blocs d'un acte déjà reçus) est un état
 * React, logé dans un composant qui ne se démonte plus jamais. Aucun `setState`
 * de store ne peut l'atteindre. Le laisser filer, c'est l'acte de A réinjecté
 * dans le premier éditeur que B ouvre — exactement la fuite que ce module
 * existe pour empêcher.
 *
 * D'où le **bail de résidence** : un numéro qui change à chaque éviction et qui
 * sert de clé React à toute la résidence. Le changer reconstruit ce qui vit
 * dedans, y compris ce qu'aucun store ne connaît.
 *
 * ## L'ordre compte
 *
 * On **abandonne d'abord** ce qui attend d'être enregistré. Ce brouillon
 * appartient à A ; le laisser partir sous l'identité de B serait au mieux
 * refusé par le serveur (l'enregistrement est gardé par cabinet + utilisateur),
 * au pire un document envoyé sous la mauvaise identité. Ensuite seulement on
 * démonte et on efface.
 */

import { create } from 'zustand';
import { useCanvasVisibiliteStore } from './canvasVisibilite';
import { redactionStreamHub } from '../streaming/redactionStreamHub';
import { useCanvasEditorStore } from './canvasEditorStore';
import { useCanvasStageStore } from './canvasStage';
import { abandonPendingCanvasOpens } from './canvasOpening';
import { oublierLesFilsConfirmes } from './canvasConversationGate';
import { oublierLesRestaurationsIndisponibles } from './canvasRestaurationIndisponible';
import { oublierLesChoixDeDepart } from './canvasChoixDeDepart';
import { oublierLesSuppressionsEnCours } from './filsEnSuppression';
import { canvasDocumentRuntime } from './canvasDocumentRuntime';

interface CanvasResidenceLeaseStore {
  /** Numéro du bail courant. Sert de clé React à la résidence du canvas. */
  lease: number;
}

const useCanvasResidenceLeaseStore = create<CanvasResidenceLeaseStore>(() => ({ lease: 0 }));

/** Le bail courant, à utiliser comme clé React de la résidence. */
export function useCanvasResidenceLease(): number {
  return useCanvasResidenceLeaseStore((s) => s.lease);
}

/**
 * À appeler au changement d'utilisateur authentifié, depuis `AuthProvider`.
 *
 * Il n'y a plus de filet local à conserver ni à purger : le stockage
 * `documentSessions`, qui gardait de quoi re-télécharger la pièce d'ORIGINE,
 * a été supprimé par #1332 — c'était le repli que le tri-état de restauration
 * interdit. La seule source de restauration est le brouillon serveur.
 */
export function evictCanvasForUserChange(): void {
  // Une requête agent peut être en train d'attendre le canvas (#860). Rien ne
  // se démonte plus, donc rien ne l'annule tout seul : sans cet abandon, elle
  // se dénouerait sur le canvas de l'avocat SUIVANT — le document du suivant,
  // livré à la demande du précédent.
  abandonPendingCanvasOpens('changement de compte');
  // Les preuves d'existence de fils sont bornées au compte qui les a obtenues :
  // ce sont des autorisations d'ouverture, pas des faits universels. Les garder
  // laisserait l'avocat B ouvrir un Canvas sur un identifiant de fil de A.
  oublierLesFilsConfirmes();
  // Les verdicts « ce document n'a pas pu être rouvert » nomment une
  // conversation, donc un fil du compte sortant : les garder ferait s'afficher
  // un refus à l'avocat suivant, sur un fil qu'il ne possède pas.
  oublierLesRestaurationsIndisponibles();
  // Même raison pour un voile de choix du papier à en-tête resté ouvert sur un fil du compte sortant.
  oublierLesChoixDeDepart();
  // Les fenêtres de suppression sont bornées au compte connecté : une
  // conversation de l'avocat A ne doit pas rester interdite d'écriture pour B.
  oublierLesSuppressionsEnCours();
  // Le runtime aussi : sa session `ready` survivrait au démontage du panneau,
  // et la première demande de l'avocat suivant se résoudrait dessus, sur une
  // instance devenue nulle.
  // Purge atomique des scopes physiques ET du registre logique. Ne purger que
  // la conversation visible laisserait les instances et writers des autres
  // documents de l'ancien compte joignables par leur conversation.
  // The runtime first invalidates every per-session draft controller, including
  // timers and late ACKs, then tears down physical scopes and logical routing.
  canvasDocumentRuntime.purgeAllSessions('changement de compte');
  useCanvasVisibiliteStore.setState({ deploye: false, aDejaEteDeploye: false, masquesEnQuittantLeFil: {} });
  // Les flux de rédaction en cours appartiennent à l'avocat qui part. Depuis
  // #857 ils vivent HORS de React : aucun démontage, aucun bail neuf ne les
  // atteint plus — seul cet abandon explicite les coupe. Sans lui, la rédaction
  // de A continuerait de s'écrire dans le canvas de B.
  redactionStreamHub.abandonnerTout('changement de compte');
  useCanvasStageStore.getState().publishStage({ conversationId: null, caseId: null });
  // Le document de chaque conversation est parti avec son scope, juste au-dessus
  // (`purgeAllSessions`) : ce store ne porte plus de présentation documentaire
  // depuis #1333, seulement la conversation du résident sélectionné.
  useCanvasEditorStore.getState().setHolderConversationId(null);
  // En dernier : le bail neuf reconstruit la résidence, donc l'état React que
  // les effacements ci-dessus ne pouvaient pas atteindre.
  useCanvasResidenceLeaseStore.setState((s) => ({ lease: s.lease + 1 }));
}
